<script lang="ts">
  import MetalFrame from '../hud/MetalFrame.svelte';
  import ShipArt from '../ShipArt.svelte';
  import { socketState } from '../socket-state.svelte';
  import { sharedAssets } from '../../assets/asset-store';
  import { atlasSignal } from '../../assets/atlas-signal.svelte';
  import { wornSkin } from '../hud/worn-skin';
  import { LOBBY_UI } from '../../game/constants/lobby';
  import type { ProfileShip } from '../../account/profile-ship';
  import {
    MAX_LOBBY_NAME_LENGTH,
    matchesLobbySearch,
    normaliseJoinCode,
    normaliseLobbyName,
    occupancyLabel,
    type LobbyListRow,
    type LobbyVisibility,
  } from '../../lobby/lobby-state';

  interface Props {
    rows: readonly LobbyListRow[];
    message?: string;
    onjoin: (joinCode: string) => void;
    oncreate: (visibility: LobbyVisibility, name: string) => void;
    onrefresh: () => void;
    onback: () => void;
  }

  const { rows, message = '', onjoin, oncreate, onrefresh, onback }: Props = $props();

  let visibility = $state<LobbyVisibility>('public');
  let code = $state('');
  let name = $state('');
  let search = $state('');

  const shown = $derived(rows.filter((row) => matchesLobbySearch(row, search)));

  $effect(() => {
    const timer = setInterval(onrefresh, LOBBY_UI.refreshMs);
    return () => {
      clearInterval(timer);
    };
  });

  function hostSkinCss(profile: ProfileShip) {
    void atlasSignal.generation;
    const skin = wornSkin(socketState.products, profile.skinProductId, 0);
    return skin === null
      ? null
      : sharedAssets.cosmeticCss(skin.imagePath, skin.shift, LOBBY_UI.portrait);
  }

  function typeCode(event: Event): void {
    const field = event.currentTarget;
    if (field instanceof HTMLInputElement) {
      code = normaliseJoinCode(field.value);
      field.value = code;
    }
  }

  function typeName(event: Event): void {
    const field = event.currentTarget;
    if (field instanceof HTMLInputElement) {
      name = field.value.slice(0, MAX_LOBBY_NAME_LENGTH);
      field.value = name;
    }
  }
</script>

<div class="browser" data-testid="lobby-browser">
  <div class="browser__panel">
    <MetalFrame />

    <div class="browser__content">
      <h2 class="browser__heading">Custom Lobbies</h2>

      <input
        type="search"
        class="browser__search"
        placeholder="Search lobbies"
        aria-label="Search lobbies"
        bind:value={search}
        data-testid="lobby-search"
      />

      <div class="browser__well">
        <div class="browser__columns">
          <span>Game</span>
          <span></span>
          <span>Creator</span>
          <span>Joined</span>
          <span></span>
        </div>

        <ul class="browser__list" data-testid="lobby-list">
          {#each shown as row (row.joinCode)}
            {@const profile = row.hostProfileShip}
            {@const skin = profile === null ? null : hostSkinCss(profile)}
            <li class="browser__row" data-testid="lobby-row">
              <span class="browser__game-name">{row.name}</span>
              <span
                class="browser__art"
                class:browser__art--empty={profile === null}
                data-testid="lobby-row-portrait"
              >
                {#if profile !== null}
                  <ShipArt
                    class="browser__layer"
                    team={0}
                    shipId={profile.shipId}
                    size={LOBBY_UI.portrait}
                  />
                {/if}
                {#if skin !== null}
                  <span
                    class="browser__layer"
                    style:background-image="url({skin.url})"
                    style:background-size={skin.backgroundSize}
                    style:background-position={skin.backgroundPosition}
                    style:width="{skin.width}px"
                    style:height="{skin.height}px"
                  ></span>
                {/if}
              </span>
              <span class="browser__host-name">{row.hostName}</span>
              <span>{occupancyLabel(row)}</span>
              <button
                class="wis-btn-inline browser__join"
                type="button"
                onclick={() => {
                  onjoin(row.joinCode);
                }}>Join</button
              >
            </li>
          {:else}
            <li class="browser__empty" data-testid="lobby-list-empty">
              {rows.length === 0 ? LOBBY_UI.emptyList : LOBBY_UI.noMatch}
            </li>
          {/each}
        </ul>
      </div>

      {#if message !== ''}
        <p class="browser__message" data-testid="lobby-browser-message">{message}</p>
      {/if}

      <div class="browser__footer">
        <div class="wis-teams browser__visibility">
          <input
            id="lobbyPublic"
            type="radio"
            name="visibility"
            value="public"
            bind:group={visibility}
          />
          <label for="lobbyPublic">Public</label>
          <input
            id="lobbyPrivate"
            type="radio"
            name="visibility"
            value="private"
            bind:group={visibility}
          />
          <label for="lobbyPrivate">Private</label>
        </div>

        <input
          type="text"
          class="browser__name"
          maxlength={MAX_LOBBY_NAME_LENGTH}
          placeholder="Lobby name"
          aria-label="Lobby name"
          value={name}
          oninput={typeName}
        />

        <button
          class="wis-btn browser__action"
          type="button"
          onclick={() => {
            oncreate(visibility, normaliseLobbyName(name));
          }}>Create</button
        >

        <input
          type="text"
          class="browser__code"
          maxlength="8"
          placeholder="Join code"
          aria-label="Join code"
          value={code}
          oninput={typeCode}
        />

        <button
          class="wis-btn browser__action"
          type="button"
          disabled={code === ''}
          onclick={() => {
            onjoin(code);
          }}>Enter</button
        >

        <button class="wis-btn browser__action" type="button" onclick={onback}>Back</button>
      </div>
    </div>
  </div>
</div>

<style>
  .browser {
    position: fixed;
    inset: 0;
    z-index: 4;
    display: grid;
    place-items: center;
    font-family: var(--wis-font);
    pointer-events: none;
  }

  .browser__panel {
    position: relative;
    pointer-events: auto;
    width: min(880px, 96vw);
    height: min(560px, 94vh);
    color: var(--wis-ink);
  }

  .browser__content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: 22px 44px 24px;
  }

  .browser__heading {
    margin: 0 0 14px;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 20px;
    font-weight: normal;
  }

  .browser__well {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 8px 10px 10px;
    background:
      repeating-linear-gradient(45deg, rgb(255 255 255 / 5%) 0 2px, transparent 2px 4px),
      repeating-linear-gradient(-45deg, rgb(0 0 0 / 38%) 0 2px, transparent 2px 4px),
      linear-gradient(#242423, #171716);
    box-shadow:
      inset 0 2px 5px rgb(0 0 0 / 85%),
      inset 0 -1px 0 rgb(255 255 255 / 14%),
      0 1px 0 rgb(255 255 255 / 22%);
  }

  .browser__columns,
  .browser__row {
    display: grid;
    grid-template-columns: minmax(120px, 2.6fr) 34px minmax(96px, 2fr) minmax(48px, 0.8fr) 78px;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
  }

  .browser__columns {
    margin-bottom: 6px;
    color: #97a0b6;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .browser__list {
    container: browser-list / inline-size;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
    scrollbar-width: thin;
  }

  .browser__row {
    flex: 0 0 auto;
    height: 42px;
    color: #e6ebf7;
    font-size: 12px;
    background: linear-gradient(
      rgb(255 255 255 / 18%),
      rgb(255 255 255 / 3%) 45%,
      rgb(0 0 0 / 18%)
    );
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 40%),
      inset 0 -1px 0 rgb(0 0 0 / 55%);
  }

  .browser__game-name {
    min-width: 0;
    color: #fff;
  }

  .browser__host-name {
    min-width: 0;
    color: #c8d2e6;
  }

  .browser__art {
    position: relative;
    width: 34px;
    height: 34px;
  }

  .browser__art--empty {
    box-shadow: inset 0 0 0 1px rgb(230 235 247 / 26%);
  }

  .browser__art :global(.browser__layer) {
    position: absolute;
    top: 0;
    left: 0;
    background-repeat: no-repeat;
  }

  .browser__message {
    flex: 0 0 auto;
    margin: 8px 0 0;
    text-align: center;
    font-size: 11px;
    color: #ff8b7a;
  }

  @container browser-list (width < 560px) {
    .browser__columns,
    .browser__row {
      gap: 6px;
      padding: 0 6px;
    }
  }

  /* The name is the only thing in the row a player has to read; below this the icon would win the fight for it. */
  @container browser-list (width < 460px) {
    .browser__columns,
    .browser__row {
      grid-template-columns: minmax(120px, 2.6fr) minmax(96px, 2fr) minmax(48px, 0.8fr) 78px;
    }

    .browser__art,
    .browser__columns > span:nth-child(2) {
      display: none;
    }
  }

  .browser__columns span,
  .browser__row span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .browser__join {
    padding: 9px 12px;
  }

  .browser__empty {
    margin: auto;
    padding: 0 20px;
    color: #97a0b6;
    font-size: 12px;
    text-align: center;
  }

  .browser__footer {
    flex: 0 0 auto;
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 14px;
  }

  .browser__visibility {
    display: flex;
    align-items: center;
  }

  .browser__visibility label {
    width: 104px;
    margin: 0;
    font-size: 11px;
    background-size: 104px 30px;
  }

  .browser__action {
    padding: 12px 22px;
  }

  .browser__code {
    width: 150px;
    height: 30px;
    padding: 0 10px;
    color: #101010;
    font-family: var(--wis-font);
    font-size: 13px;
    letter-spacing: 3px;
    text-transform: uppercase;
    background-color: transparent;
    background-image: url('../../../assets/legacy/UIAssets/TextField_1.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    border-style: none;
    outline: none;
  }

  .browser__code::placeholder {
    color: #2b2b2b;
  }

  .browser__search,
  .browser__name {
    height: 30px;
    padding: 0 10px;
    color: #101010;
    font-family: var(--wis-font);
    font-size: 12px;
    background-color: transparent;
    background-image: url('../../../assets/legacy/UIAssets/TextField_1.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    border-style: none;
    outline: none;
  }

  .browser__search {
    align-self: center;
    width: min(280px, 60%);
    margin-bottom: 10px;
  }

  .browser__name {
    width: 190px;
  }

  .browser__search::placeholder,
  .browser__name::placeholder {
    color: #2b2b2b;
  }
</style>
